import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, PenLine, GripVertical, ChevronDown, ChevronUp } from "lucide-react";
import { DndContext, useDraggable, DragEndEvent } from "@dnd-kit/core";
import { RecordingPlayer } from "../../recording/RecordingPlayer";

interface PaneNoteStickyCardProps {
  noteText: string;
  recordings?: { audio?: any; video?: any } | null;
  creatorName?: string;
  /** Unique ID for current pane — when this changes, card re-shows if notes exist */
  activityId?: string;
  onDismiss?: () => void;
  /** When flipped to true, re-shows the card even if already dismissed/seen */
  forceVisible?: boolean;
  /** Whether to auto-show on pane load (default true). Set false for on_demand mode. */
  autoShow?: boolean;
}

const hasContent = (html: string | undefined | null): boolean => {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, "").trim().length > 0;
};

const DRAG_ID = "pane-note-sticky-card";

const StickyCardInner: React.FC<
  PaneNoteStickyCardProps & {
    position: { x: number; y: number };
    collapsed: boolean;
    onToggleCollapse: () => void;
    onDismiss: () => void;
  }
> = ({
  noteText,
  recordings,
  creatorName,
  position,
  collapsed,
  onToggleCollapse,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const hasRecordings = recordings?.audio || recordings?.video;

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: DRAG_ID });

  const style = {
    transform: transform
      ? `translate3d(${position.x + transform.x}px, ${position.y + transform.y}px, 0)`
      : `translate3d(${position.x}px, ${position.y}px, 0)`,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[300px] ${
        isDragging ? "cursor-grabbing" : ""
      }`}
    >
      <div className="rounded-xl border-2 border-amber-300 bg-amber-50 shadow-2xl overflow-hidden">
        {/* Drag handle — desktop only */}
        <div
          {...listeners}
          {...attributes}
          className="hidden sm:flex items-center justify-center py-1 border-b border-amber-200 bg-amber-100/60 cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="w-4 h-4 text-amber-400" />
        </div>

        {/* Header row */}
        <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-100/80">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-amber-200 flex-shrink-0 shadow-sm">
            <PenLine className="w-3.5 h-3.5 text-amber-700" />
          </span>

          <span className="flex-1 text-xs font-semibold text-amber-800 leading-snug">
            {creatorName
              ? t("note_from_author_named", { name: creatorName }) ||
                `Note from ${creatorName}`
              : t("note_from_author") || "Note from author"}
          </span>

          <button
            type="button"
            onClick={onToggleCollapse}
            className="p-1 rounded hover:bg-amber-200 transition-colors flex-shrink-0"
            aria-label={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? (
              <ChevronDown className="w-3.5 h-3.5 text-amber-600" />
            ) : (
              <ChevronUp className="w-3.5 h-3.5 text-amber-600" />
            )}
          </button>

          <button
            type="button"
            onClick={onDismiss}
            className="p-1 rounded hover:bg-amber-200 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5 text-amber-600" />
          </button>
        </div>

        {/* Expandable body */}
        {!collapsed && (
          <div className="px-3 pb-3 pt-2 border-t border-amber-200 bg-amber-50">
            {hasContent(noteText) && (
              <div
                className="text-sm text-amber-900 prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1"
                dangerouslySetInnerHTML={{ __html: noteText }}
              />
            )}
            {hasRecordings && (
              <div className="mt-2 flex flex-wrap gap-2">
                {recordings?.audio && (
                  <RecordingPlayer
                    recording={recordings.audio}
                    type="audio"
                    compact
                    readOnly
                  />
                )}
                {recordings?.video && (
                  <RecordingPlayer
                    recording={recordings.video}
                    type="video"
                    compact
                    readOnly
                  />
                )}
              </div>
            )}
            <p className="mt-2.5 text-[10px] text-amber-600/70 text-center italic">
              {t("example_note_hint") || "Author's note for this pane"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const PaneNoteStickyCard: React.FC<PaneNoteStickyCardProps> = ({
  noteText,
  recordings,
  creatorName,
  activityId,
  onDismiss,
  forceVisible,
  autoShow = true,
}) => {
  const [visible, setVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const seenRef = useRef<Set<string>>(new Set());

  const hasRecordings = recordings?.audio || recordings?.video;
  const hasNote = hasContent(noteText) || !!hasRecordings;

  useEffect(() => {
    if (!activityId || !hasNote || !autoShow) {
      setVisible(false);
      return;
    }
    if (seenRef.current.has(activityId)) return;
    seenRef.current.add(activityId);
    setCollapsed(false);
    setPosition({ x: 0, y: 0 });
    setVisible(true);
  }, [activityId, hasNote, autoShow]);

  useEffect(() => {
    if (forceVisible && hasNote) {
      setCollapsed(false);
      setVisible(true);
    }
  }, [forceVisible]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { delta } = event;
    if (delta) {
      setPosition((prev) => ({
        x: prev.x + delta.x,
        y: prev.y + delta.y,
      }));
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    onDismiss?.();
  };

  if (!visible) return null;

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <StickyCardInner
        noteText={noteText}
        recordings={recordings}
        creatorName={creatorName}
        position={position}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((v) => !v)}
        onDismiss={handleDismiss}
      />
    </DndContext>
  );
};

export default PaneNoteStickyCard;
